import { Badge, Divider, Group, PasswordInput, Text, Title } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useQuery } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'

// The slice of the host form this component reads and writes (same casting
// convention as OrganizationStripeSection).
export type BridgeHostForm = UseFormReturnType<{
  bridgeApiKey: string
  bridgeWebhookSecret: string
}>

interface OrganizationBridgeSectionProps {
  organizationId?: string
  form: BridgeHostForm
}

export function OrganizationBridgeSection({
  organizationId,
  form,
}: OrganizationBridgeSectionProps) {
  const orpc = useOrpc()

  const { data: status } = useQuery(
    orpc.getOrganizationBridgeStatus.queryOptions({
      input: { organizationId: organizationId ?? '' },
      enabled: Boolean(organizationId),
    }),
  )

  const configured = status?.configured === true
  const environment = status?.environment
  const webhookSecretConfigured = status?.webhookSecretConfigured === true

  return (
    <>
      <Divider my="sm" />
      <Group justify="space-between" mb="xs">
        <Title order={4}>Bridge (Insurance &amp; Claims)</Title>
        {/* There is no shared fallback key: Bridge issues one per organization, so an
            organization without one cannot reach Bridge at all — empty is "off", not
            "default". The environment is shown because it is the mistake that actually
            happens: a sandbox key pasted into a production organization. */}
        {configured ? (
          <Badge color={environment === 'production' ? 'green' : 'yellow'} variant="light">
            {environment === 'production' ? 'Configured (Production)' : 'Configured (Sandbox)'}
          </Badge>
        ) : (
          <Badge color="gray" variant="light">
            Not set up
          </Badge>
        )}
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        {configured
          ? 'A key is saved. Enter a new one below to replace it.'
          : 'This organization cannot use Bridge until an API key is saved here.'}
      </Text>

      <PasswordInput
        label="Bridge API key"
        placeholder="sk_…"
        autoComplete="new-password"
        {...form.getInputProps('bridgeApiKey')}
      />
      <Text size="xs" c="dimmed" mb="md">
        The key is <strong>checked against Bridge before it is saved</strong>, so a wrong one is
        refused here rather than surfacing later as a failed claim. Sandbox keys (
        <code>sk_sandbox_…</code>) and production keys reach different Bridge environments; the
        badge above says which one this organization is pointed at. The key is never shown again.
      </Text>

      <Group justify="space-between" mb="xs">
        <Text size="sm" fw={500}>
          Webhook secret
        </Text>
        {/* Never displayed once saved (the status endpoint doesn't return the value either) —
            just whether one is on file, same "set / not set" shape as the API key badge above. */}
        {webhookSecretConfigured ? (
          <Badge color="green" variant="light">
            Set
          </Badge>
        ) : (
          <Badge color="gray" variant="light">
            Not set
          </Badge>
        )}
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        {webhookSecretConfigured
          ? 'A secret is saved. Enter a new one below to replace it.'
          : 'Bridge webhook events are accepted unsigned for this organization until a secret is saved here.'}
      </Text>

      <PasswordInput
        label="Bridge webhook secret"
        placeholder="whsec_…"
        autoComplete="new-password"
        {...form.getInputProps('bridgeWebhookSecret')}
      />
      <Text size="xs" c="dimmed">
        Copied from Bridge&apos;s dashboard after registering this organization&apos;s webhook
        endpoint. Saved together with the API key above — there is no separate save for this field,
        so re-enter the API key when updating just the secret. The secret is never shown again once
        saved.
      </Text>
    </>
  )
}
